import { OptionsApi } from '@core/swagger-client';
import { PollWithPoints } from '@core/types';
import { Button, Close, Confirm, Delete, Input, Plus, Switch } from '@core/ui';
import { useParams } from '@online-webinar/router';
import { Link } from '@online-webinar/ui-frontend';
import { useMutation } from '@tanstack/react-query';
import { cloneDeep, isEqual } from 'lodash-es';
import { useEffect, useState } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { AddPollAnswerButton } from '../../AddPollAnswerButton';
import { Poll } from '../../Poll';
import { PollAnswer } from '../../PollAnswer';
import { PollAnswerHeader } from '../../PollAnswerHeader';
import { PollAnswerLabel } from '../../PollAnswerLabel';
import { PollAnswers } from '../../PollAnswers';
import { PollCard } from '../../PollCard';
import { PollControl } from '../../PollControl';
import { PollQuestion } from '../../PollQuestion';
import { PollQuestionNumber } from '../../PollQuestionNumber';

export const EditPoll = ({
    poll,
    pollIndex,
    updateOnly,
    onUpdate,
    onDelete
}: {
    poll: PollWithPoints;
    pollIndex: number;
    updateOnly: boolean;
    onUpdate: (poll: PollWithPoints, pollIndex: number) => void;
    onDelete: (poll: PollWithPoints) => void;
}) => {
    const { webinarId } = useParams({ strict: false });

    const { t } = useTranslation();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [pollData, setPollData] = useState<PollWithPoints>(poll);

    const { control, register, watch, reset, handleSubmit } = useForm<PollWithPoints>({ mode: 'all', reValidateMode: 'onChange', defaultValues: poll });
    const { fields, append, remove } = useFieldArray({ control, name: 'options' });

    const updatedPoll = watch();
    const question = watch('question');
    const applyWeighting = watch('applyWeighting');
    const publicResults = watch('publicResults');
    const onSubmit: SubmitHandler<PollWithPoints> = (data) => {};

    const AlphaIndex = (index: number) => {
        return String.fromCharCode(65 + (index % 26));
    };

    const updatePollMutation = useMutation(OptionsApi.poll.edit());
    const updatePoll = (values: PollWithPoints) => {
        updatePollMutation.mutate({
            webinarId: webinarId!,
            ...values
        });
    };

    const requestDelete = () => {
        if (question === '' && fields.length === 0) {
            onDelete(poll);
            return;
        }

        setShowDeleteConfirmation(true);
    };

    useEffect(() => {
        if (updatedPoll?.pollId !== poll.pollId) {
            setPollData(poll);
            reset(poll);
        }
    }, [poll]);

    useEffect(() => {
        if (!isEqual(pollData, updatedPoll)) {
            // We need a cloned object to detect changes
            setPollData(cloneDeep(updatedPoll));
        }
    }, [pollData, updatedPoll]);

    useEffect(() => {
        if (isEqual(pollData, poll)) {
            return;
        }

        onUpdate(pollData, pollIndex);
        const timeout = setTimeout(() => {
            updatePoll(pollData);
        }, 200);
        return () => clearTimeout(timeout);
    }, [pollData]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <PollCard>
                <Poll>
                    <PollControl>
                        <PollQuestionNumber pollQuestionNumber={pollIndex + 1} />
                    </PollControl>
                    <div className="flex-1 flex items-start flex-wrap gap-4">
                        <div className="relative flex-2 min-w-80 bg-white rounded-b-lg rounded-tr-lg p-6">
                            <PollQuestion>
                                <Input
                                    type="text"
                                    autoFocus
                                    label={t('Content.Poll.QuestionLabel')}
                                    hideLabel
                                    placeholder={t('Content.Poll.QuestionLabel')}
                                    {...register('question')}
                                    classNames="mb-6"
                                    readOnly={updateOnly}
                                />
                            </PollQuestion>
                            <PollAnswers>
                                <PollAnswerHeader isUpdateOnly={updateOnly} />
                                <div className="flex flex-col">
                                    {fields.map((field, index) => (
                                        <PollAnswer key={field.id}>
                                            <div className="flex items-baseline gap-x-2 mt-3">
                                                <PollAnswerLabel pollAnswerLabel={AlphaIndex(index)} />
                                                <div className="flex-1 flex items-start gap-x-2">
                                                    <Input
                                                        type="text"
                                                        classNames="mb-6"
                                                        label={t('Content.Poll.OptionLabel', { index: pollIndex + 1, char: AlphaIndex(index) })}
                                                        hideLabel
                                                        {...register(`options.${index}.text`)}
                                                        placeholder={t('Content.Poll.OptionPlaceholder')}
                                                        readOnly={updateOnly}
                                                    />
                                                    <Input
                                                        type="number"
                                                        {...register(`options.${index}.points`)}
                                                        classNames="basis-20"
                                                        label={t('Content.Poll.PointsLabel', { index: pollIndex + 1, char: AlphaIndex(index) })}
                                                        hideLabel
                                                        min={0}
                                                        max={100}
                                                        placeholder={t('Content.Poll.PointsPlaceholder')}
                                                    />
                                                    {!updateOnly && (
                                                        <Button type="button" iconbtn onClick={() => remove(index)}>
                                                            <Close classNames="size-6 stroke-warning" />
                                                            <span className="sr-only">{t('Content.Poll.Button.DeletePollAnswer')}</span>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </PollAnswer>
                                    ))}{' '}
                                    {!updateOnly && (
                                        <div className="flex justify-between gap-4">
                                            <AddPollAnswerButton onClick={() => append({ text: '', optionId: crypto.randomUUID(), points: undefined })}>
                                                <Plus classNames="size-5 stroke-current" />
                                            </AddPollAnswerButton>
                                        </div>
                                    )}
                                </div>
                                <Confirm
                                    title={t('Content.Poll.ConfirmDelete.Title')}
                                    confirmTestId="poll-delete-confirm"
                                    closeTestId="poll-delete-cancel"
                                    open={showDeleteConfirmation}
                                    onClose={() => setShowDeleteConfirmation(false)}
                                    onConfirm={() => onDelete(poll)}
                                >
                                    <Trans i18nKey="Content.Poll.ConfirmDelete.Msg" values={{ poll: question }} />
                                </Confirm>
                            </PollAnswers>
                        </div>
                        <div className="sticky top-20 flex-1 bg-white rounded-lg flex flex-col justify-between p-6">
                            <div>
                                <Switch classNames="mb-6" label={t('Content.Poll.Option.PublicResults')} {...register('publicResults')} />
                                {/* Only applies to shown results, but stays discoverable (and keeps its value) while they are hidden */}
                                <Switch
                                    classNames="mb-6"
                                    label={t('Content.Poll.Option.ShowPercentagesOnly')}
                                    aria-disabled={!publicResults}
                                    disabledReason={t('Content.Poll.Option.ShowPercentagesOnly.DisabledReason')}
                                    {...register('showPercentagesOnly')}
                                />
                                <Switch classNames="mb-6" label={t('Content.Poll.Option.ApplyWeighting')} {...register('applyWeighting')} />
                                {applyWeighting && (
                                    <Link
                                        to="/$webinarId/polls/$pollId/weighting"
                                        params={{ webinarId, pollId: poll.pollId }}
                                        classNames="w-full mb-6"
                                        variant="primary-outline"
                                    >
                                        {t('Content.Poll.Link.WeightinFactor')}
                                    </Link>
                                )}
                            </div>
                            {!updateOnly && (
                                <Button
                                    data-testid="poll-delete-button"
                                    variant="warning-outline"
                                    onClick={requestDelete}
                                    aria-label={t('Content.Poll.Button.DeletePollAriaLabel', { poll: question })}
                                >
                                    <Delete classNames="size-6 stroke-current mr-2" />
                                    {t('Content.Poll.Button.DeletePoll')}
                                </Button>
                            )}
                        </div>
                    </div>
                </Poll>
            </PollCard>
        </form>
    );
};
